import { useMemo, useState } from "react";
import { useLocation } from "wouter";
import {
  useListLeads,
  useListAccounts,
  type Lead,
} from "@workspace/api-client-react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Skeleton } from "@/components/ui/skeleton";
import { Search } from "lucide-react";

/**
 * 2026-06-10 redesign slice 2 — Add Account entry point on /accounts.
 *
 * Flow:
 *   1. Operator clicks "Add Account" on the Accounts list page
 *   2. This dialog opens with a Search input + scrollable list filtered to
 *      LEADS that:
 *      - have stage === "closed_won"
 *      - AND do NOT yet have an Account converted from them
 *   3. Operator picks a lead → this dialog closes → operator is navigated
 *      to the Convert Lead PAGE (/accounts/convert/:leadId) which hosts
 *      the full conversion form full-viewport. (Replaced the previous
 *      side-Sheet chain — operators kept scrolling past its bottom edge
 *      with the plan picker open.)
 *
 * Data sourcing:
 *   - useListLeads({ stage: "closed_won", pageSize: 200 }) — server-filtered.
 *     Returns the LeadsListResponse envelope {items, total, page, pageSize};
 *     consume .items, NOT the raw `.data` (asymmetric with useListAccounts
 *     which IS raw). Got that wrong in a prior rev and white-screened the
 *     dialog — comment retained as a tripwire for future-me.
 *   - useListAccounts({ pageSize: 200 }) — used to compute the set of
 *     already-converted lead ids (account.convertedFromLeadId).
 */

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export function AddAccountDialog({ open, onOpenChange }: Props) {
  const [, navigate] = useLocation();
  const [search, setSearch] = useState("");

  const leadsQuery = useListLeads(
    { stage: "closed_won", pageSize: 200 },
    {
      query: {
        queryKey: ["/api/leads", { stage: "closed_won", forAddAccount: true }],
        enabled: open,
      },
    },
  );

  const accountsQuery = useListAccounts(
    { pageSize: 200 },
    {
      query: {
        queryKey: ["/api/accounts", { forAddAccount: true }],
        enabled: open,
      },
    },
  );

  const convertedLeadIds = useMemo<Set<number>>(() => {
    const set = new Set<number>();
    for (const a of accountsQuery.data ?? []) {
      if (a.convertedFromLeadId != null) set.add(a.convertedFromLeadId);
    }
    return set;
  }, [accountsQuery.data]);

  const eligibleLeads = useMemo<Lead[]>(() => {
    // useListLeads returns the envelope {items, total, page, pageSize},
    // NOT a raw Lead[] — consume .items. See module header comment.
    const leads: Lead[] = leadsQuery.data?.items ?? [];
    const unconverted = leads.filter((l: Lead) => !convertedLeadIds.has(l.id));
    const q = search.trim().toLowerCase();
    if (!q) return unconverted;
    return unconverted.filter((l: Lead) => {
      const haystack = [
        l.customerName,
        l.mobileNumber,
        l.natureOfBusiness ?? "",
        l.email ?? "",
      ]
        .join(" ")
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [leadsQuery.data, convertedLeadIds, search]);

  const isLoading = leadsQuery.isLoading || accountsQuery.isLoading;

  function handleSelectLead(lead: Lead) {
    onOpenChange(false);
    setSearch("");
    navigate(`/accounts/convert/${lead.id}`);
  }

  function handleDialogChange(next: boolean) {
    if (!next) setSearch("");
    onOpenChange(next);
  }

  return (
    <Dialog open={open} onOpenChange={handleDialogChange}>
      <DialogContent className="max-w-xl">
        <DialogHeader>
          <DialogTitle>Add Account</DialogTitle>
          <DialogDescription>
            Pick a closed-won lead to convert into an account. Only leads
            that have not been converted yet are shown.
          </DialogDescription>
        </DialogHeader>

        <div className="relative">
          <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            autoFocus
            placeholder="Search by name, phone, business…"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-9"
            data-testid="add-account-search"
          />
        </div>

        <div className="max-h-[360px] overflow-y-auto rounded-md border">
          {isLoading ? (
            <div className="space-y-2 p-3">
              {[...Array(4)].map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : eligibleLeads.length === 0 ? (
            <div className="p-6 text-center text-sm text-muted-foreground">
              {search.trim()
                ? "No leads match your search."
                : "No closed-won leads available. Either there are no leads in closed-won, or all closed-won leads have already been converted into accounts."}
            </div>
          ) : (
            <ul className="divide-y">
              {eligibleLeads.map((lead: Lead) => (
                <li key={lead.id}>
                  <button
                    type="button"
                    onClick={() => handleSelectLead(lead)}
                    className="flex w-full items-center justify-between gap-2 px-3 py-2.5 text-left hover:bg-accent focus:bg-accent focus:outline-none"
                    data-testid={`add-account-lead-${lead.id}`}
                  >
                    <div className="flex flex-col min-w-0">
                      <span className="font-medium text-sm truncate">
                        {lead.customerName}
                      </span>
                      <span className="text-[11px] text-muted-foreground truncate">
                        {lead.mobileNumber}
                        {lead.natureOfBusiness
                          ? ` · ${lead.natureOfBusiness}`
                          : ""}
                      </span>
                    </div>
                    <Badge variant="outline" className="shrink-0 text-[10px]">
                      {lead.spaceType}
                    </Badge>
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
